/**
 * MultiModelComparison
 * 
 * Renders multiple AI model responses side-by-side so the user can compare
 * and select the best one. Each card streams independently and shows a
 * "Select this response" button once complete.
 */

import React from 'react';
import { Check, Loader2, AlertCircle, Clock, Sparkles } from 'lucide-react';
import styles from './MultiModelComparison.module.css';

export interface ModelResponse {
  model: string;
  content: string;
  status: 'streaming' | 'completed' | 'error';
  latencyMs?: number;
  error?: string;
}

export interface MultiModelComparisonProps {
  /** Map of model ID → accumulated response */
  responses: Record<string, ModelResponse>;
  /** Which model the user has selected (null = none yet) */
  selectedModel: string | null;
  /** Called when the user clicks "Select" on a model card */
  onSelectModel: (model: string) => void;
  /** Whether the overall comparison is still streaming */
  isComparing: boolean;
  /** Display-friendly model name lookup */
  modelDisplayNames?: Record<string, string>;
}

export const MultiModelComparison: React.FC<MultiModelComparisonProps> = ({
  responses,
  selectedModel,
  onSelectModel,
  isComparing,
  modelDisplayNames = {},
}) => {
  const modelIds = Object.keys(responses);

  const getDisplayName = (modelId: string): string => {
    return modelDisplayNames[modelId] || modelId;
  };

  const formatLatency = (ms?: number): string => {
    if (!ms) return '';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Sparkles size={16} className={styles.headerIcon} />
        <span className={styles.headerTitle}>Model Comparison</span>
        <span className={styles.headerSubtitle}>
          {isComparing
            ? 'Streaming responses from multiple models...'
            : selectedModel
              ? `You selected ${getDisplayName(selectedModel)}`
              : 'Select the response you prefer'}
        </span>
      </div>

      <div className={styles.grid} style={{ gridTemplateColumns: `repeat(${modelIds.length}, 1fr)` }}>
        {modelIds.map((modelId) => {
          const resp = responses[modelId];
          const isSelected = selectedModel === modelId;
          const isOtherSelected = selectedModel !== null && selectedModel !== modelId;

          return (
            <div
              key={modelId}
              className={`${styles.card} ${isSelected ? styles.cardSelected : ''} ${isOtherSelected ? styles.cardDimmed : ''}`}
            >
              <div className={styles.cardHeader}>
                <div className={styles.modelInfo}>
                  <span className={styles.modelName}>{getDisplayName(modelId)}</span>
                  <div className={styles.modelMeta}>
                    {resp.status === 'streaming' && (
                      <span className={styles.statusStreaming}>
                        <Loader2 size={12} className={styles.spinner} />
                        Streaming...
                      </span>
                    )}
                    {resp.status === 'completed' && resp.latencyMs && (
                      <span className={styles.statusDone}>
                        <Clock size={12} />
                        {formatLatency(resp.latencyMs)}
                      </span>
                    )}
                    {resp.status === 'error' && (
                      <span className={styles.statusError}>
                        <AlertCircle size={12} />
                        Error
                      </span>
                    )}
                  </div>
                </div>

                {resp.status === 'completed' && !selectedModel && (
                  <button
                    className={styles.selectButton}
                    onClick={() => onSelectModel(modelId)}
                  >
                    <Check size={14} />
                    Select
                  </button>
                )}

                {isSelected && (
                  <div className={styles.selectedBadge}>
                    <Check size={14} />
                    Selected
                  </div>
                )}
              </div>

              <div className={styles.cardContent}>
                {resp.error ? (
                  <div className={styles.errorMessage}>{resp.error}</div>
                ) : (
                  <div className={styles.responseText}>
                    {resp.content}
                    {resp.status === 'streaming' && (
                      <span className={styles.cursor}>▊</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MultiModelComparison;
